/* eslint-disable no-restricted-imports */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import { Spinner } from '@/components/Elements';
import { Head } from '@/components/Head';
import { ConfirmDelete } from '@/components/Modal';
import { Table } from '@/components/Table';
import { useGetAllLoans } from '@/features/loans/hooks';
import { useGetAllWallets } from '@/features/wallet/hooks';
import { ErrorProps } from '@/types';
import { formatDate } from '@/utils/functions';
import { notifyError, notifySuccess } from '@/utils/notifications';

import { ConfirmSuspend } from '../components/ConfirmSuspend';
import { TableMenu } from '../components/TableMenu';
import {
  useDeleteUser,
  useGetUsers,
  useGetUsersStats,
  useSuspendUser,
} from '../hooks';

export const Users = () => {
  const [selectedSuspended, setSelectedSuspended] = useState<string>(''); // filter state
  const [selectedStatus, setSelectedStatus] = React.useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchName, setSearchName] = useState('');

  const [populatedUsers, setPopulatedUsers] = useState<any>(null);
  const { data: users, isLoading: isUsersLoading } = useGetUsers({
    isActivated: selectedStatus || undefined,
    isSuspended: selectedSuspended || undefined,
    email: searchEmail || undefined,
    name: searchName || undefined, // ✅ new filter
  });

  const { data: usersStats } = useGetUsersStats();
  const { data: loans, isLoading: isLoansLoading } = useGetAllLoans();
  const { data: wallets, isLoading: isWalletsLoading } = useGetAllWallets();
  const [isDeleteConfirmationOpen, toggleIsDeleteConfirmationOpen] =
    useState(false);
  const [isSuspendConfirmationOpen, toggleIsSuspendConfirmationOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState('');

  const {
    mutate: deleteUser,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
    error: deleteError,
    reset: deleteReset,
  } = useDeleteUser();

  const {
    mutate: suspendUser,
    isLoading: isSuspendLoading,
    isSuccess: isSuspendSuccess,
    isError: isSuspendError,
    error: suspendError,
    reset: suspendReset,
  } = useSuspendUser();

  useEffect(() => {
    if (users && loans && wallets) {
      const populatedData = users.map((user: any) => ({
        ...user,
        wallet: wallets.find((wallet: any) => wallet.user?._id === user._id),
        loans: loans.resource.find((loan: any) => loan.user?._id === user._id),
      }));

      setPopulatedUsers(populatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUsersLoading,
    isLoansLoading,
    isWalletsLoading,
    isDeleteSuccess,
    users,
  ]);

  const handleSuspend = useCallback((_id: string) => {
    setSelectedUser(_id);
    toggleIsSuspendConfirmationOpen(true);
  }, []);

  const handleDelete = useCallback((_id: string) => {
    setSelectedUser(_id);
    toggleIsDeleteConfirmationOpen(true);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(
    () => [
      {
        Header: '',
        id: 'menu',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <div className=''>
            <TableMenu
              _id={row.original._id}
              handleSuspend={() => handleSuspend(row.original._id)}
              handleDelete={() => handleDelete(row.original._id)}
              isSuspendeded={row.original.isSuspended}
            />
          </div>
        ),
      },
      {
        Header: 'First Name',
        accessor: 'firstName',
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
      },
      {
        Header: 'Country',
        accessor: 'country',
      },
      {
        Header: 'Joined Date',
        Cell: ({ row }: any) => {
          return <span>{formatDate(new Date(row.original.createdAt))}</span>;
        },
      },
      {
        Header: 'Is Suspended?',
        Cell: ({ row }: any) => {
          return (
            <span
              className={
                row.original.isSuspended ? 'text-red-500' : 'text-green-500'
              }
            >
              {row.original.isSuspended ? 'Yes' : 'No'}
            </span>
          );
        },
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
      },

      {
        Header: 'Status',
        Cell: ({ row }: any) => {
          return (
            <span
              className={
                row.original.isActivated ? 'text-green-500' : 'text-red-500'
              }
            >
              {row.original.isActivated ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },

      {
        Header: 'Referrals',
        accessor: 'referralCount',
      },
      {
        Header: 'Netpoints',
        accessor: 'wallet.netPoints',
      },
      {
        Header: 'Rank',
        accessor: 'wallet.networkerRank',
      },
      {
        Header: 'Reserved Capital(₦)',
        Cell: ({ row }: any) => {
          return (
            <span>
              {row.original.wallet?.promotionalBalance.toLocaleString()}
            </span>
          );
        },
      },
      {
        Header: 'Main Wallet(₦)',
        Cell: ({ row }: any) => {
          return <span>{row.original.wallet?.amount.toLocaleString()}</span>;
        },
      },
      // {
      //   Header: 'Portfolio Value',
      //   accessor: ''
      // },
      // {
      //   Header: 'Unmature ROI',
      //   accessor: ''
      // },
      // {
      //   Header: 'Awap Project Unit (APU)',
      //   accessor: ''
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     setSearchName(searchName.trim());
  //   }, 500);

  //   return () => clearTimeout(timeout);
  // }, [searchName]);

  if (isDeleteSuccess) {
    notifySuccess('User has been deleted successfully', false);
    deleteReset();
  }

  if (isDeleteError) {
    notifyError(
      (deleteError as ErrorProps).response?.data.errors[0].msg ||
        (deleteError as ErrorProps).message,
      false,
    );
    deleteReset();
  }

  if (isSuspendSuccess) {
    notifySuccess('Operation successful', false);
    suspendReset();
  }

  if (isSuspendError) {
    console.log(suspendError);

    notifyError(
      (suspendError as ErrorProps).response?.data.errors[0].msg ||
        (suspendError as ErrorProps).message,
      false,
    );
    suspendReset();
  }

  return (
    <div>
      <Head title='Users' />
      {isUsersLoading || isLoansLoading || isWalletsLoading ? (
        <div className='flex h-screen justify-center items-center'>
          <Spinner size='md' />
        </div>
      ) : (
        <div className='overflow-hidden '>
          {usersStats && (
            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 p-4'>
              {usersStats.map((stat: any, idx: number) => (
                <div
                  key={idx}
                  className='bg-white shadow-md rounded-xl p-4 border border-gray-200 hover:shadow-lg transition'
                >
                  <h3 className='text-lg font-semibold text-indigo-600 mb-2'>
                    {stat.status.toUpperCase()}
                  </h3>
                  <p className='text-gray-700 text-sm'>
                    <span className='font-medium'>Count</span> {stat.count}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className='flex flex-wrap gap-4 px-4 mb-4'>
            {/* Search by Email */}
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Search by Email
              </label>
              <input
                id='email'
                type='text'
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder='Enter email'
                className='border border-gray-300 rounded-md p-2 w-64'
              />
            </div>

            {/* Filter by Name */}
            <div>
              <label
                htmlFor='name'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Search by Name
              </label>
              <input
                id='name'
                type='text'
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder='Enter first or last name'
                className='border border-gray-300 rounded-md p-2 w-64'
              />
            </div>
            {/* Filter by active */}
            <div>
              <label
                htmlFor='suspended'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Filter by Suspended
              </label>
              <select
                id='suspended'
                value={selectedSuspended}
                onChange={(e) => setSelectedSuspended(e.target.value)}
                className='border border-gray-300 rounded-md p-2 w-64'
              >
                <option value=''>All</option>
                <option value='true'>Suspended</option>
                <option value='false'>Active</option>
              </select>
            </div>

            {/* Filter by Status */}
            <div>
              <label
                htmlFor='status'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Filter by Status
              </label>
              <select
                id='status'
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className='border border-gray-300 rounded-md p-2 w-64'
              >
                <option value=''>All</option>
                <option value='true'>Active</option>
                <option value='false'>Inactive</option>
              </select>
            </div>
          </div>

          <Table
            data={populatedUsers ?? []}
            columns={columns}
            showPagination={true}
          />
        </div>
      )}
      <ConfirmDelete
        isOpen={isDeleteConfirmationOpen}
        setIsOpen={toggleIsDeleteConfirmationOpen}
        deleteDocument={deleteUser}
        isLoading={isDeleteLoading}
        selectedData={selectedUser}
      />

      <ConfirmSuspend
        isOpen={isSuspendConfirmationOpen}
        setIsOpen={toggleIsSuspendConfirmationOpen}
        suspendDocument={suspendUser}
        isLoading={isSuspendLoading}
        selectedData={selectedUser}
      />
      <ToastContainer />
    </div>
  );
};
